import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Tooltip, Typography } from "@mui/material";
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import PreviewIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from "@mui/icons-material/Delete";
import FolderIcon from "@mui/icons-material/Folder";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DOCUMENT_TYPE } from "../../constants/documentType";
import { DocumentField } from "../../constants/documentField";
import { DocumentFormMap } from "../../constants/documentFormMap";
import { documentServices } from "../../services/documentServices";
import { userServices } from "../../services/userServices";
import { useSelector } from "react-redux";
import ConfirmationDialog from "../common/ConfirmationDialog";
import { useSnackbar } from "../../utils/useSnackbar";
import { useTranslation } from "react-i18next";
import PageHeader from "../ui/PageHeader";
import ActionProgressDialog from '../common/ActionProgressDialog';
import "../../css/Document.css";
import "../../css/Dialog.css";

const DocumentForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const loginUser = useSelector((state) => state.userDetails.data);
    const [documents, setDocuments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [employeeId, setEmployeeId] = useState("");
    const [documentId, setDocumentId] = useState(null);
    const [documentType, setDocumentType] = useState("");
    const [fieldData, setFieldData] = useState({});
    const [openDocumentDialog, setOpenDocumentDialog] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadMessage, setDownloadMessage] = useState('');
    const { showSnackbar, SnackbarComponent } = useSnackbar();

    useEffect(() => {
        const loadData = async () => {
            try {
                const employeeResponse = await userServices.getAllUsersWhichHasNoAdminRole();
                setEmployees(employeeResponse);
            } catch {
                showSnackbar("Failed to load required data", "error");
            }
        };

        loadData();
    }, [showSnackbar]);

    useEffect(() => {
        if (!employeeId) {
            setDocuments([]);
            return;
        }

        const loadDocuments = async () => {
            try {
                const data = await documentServices.getAllDocumentByUserId(employeeId);
                setDocuments(data);
            } catch (e) {
                showSnackbar("Failed to load documents", "error");
            }
        };

        loadDocuments();
    }, [employeeId, showSnackbar]);

    useEffect(() => {
        if (!location.state) return;

        const { id, employeeId, documentType, fieldData } = location.state;

        setDocumentId(id || null);
        setEmployeeId(employeeId || "");
        setDocumentType(documentType || "");
        setFieldData(fieldData || {});

    }, [location.state]);

    const handleDocumentTypeChange = async (type) => {
        setDocumentType(type);
        setDocumentId(null);

        const selectedEmployee = employees.find(emp => emp.id === employeeId);
        const initialFieldData = {};

        const addressRequiredTypes = [
            "RELIEVING_LETTER",
            "APPOINTMENT_LETTER",
            "NURTURE_APPOINTMENT_LETTER",
            "NURTURE_SECURITY_CHEQUE_DEPOSIT_LETTER",
            "SECURITY_CHEQUE_DEPOSIT_LETTER",
            "EMPLOYMENT_BOND_AGREEMENT_LETTER"
        ];

        if (selectedEmployee) {
            // const fullName = `${selectedEmployee.firstName} ${selectedEmployee.lastName}`;
            // const address = selectedEmployee.address;
            // initialFieldData.employee_name = fullName;
            const capitalize = (str) =>
                str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";
            const firstName = capitalize(selectedEmployee.firstName);
            const lastName = capitalize(selectedEmployee.lastName);

            const fullName = `${firstName} ${lastName}`;
            const address = selectedEmployee.address;

            initialFieldData.employee_name = fullName;

            if (addressRequiredTypes.includes(type)) {
                initialFieldData.employee_address = address;
            }

            if (type === "EXPERIENCE_LETTER") {
                try {
                    const count = await documentServices.getDocumentCount(employeeId, "APPRAISAL_LETTER");
                    initialFieldData.number_of_promotion = count || 0;
                } catch (error) {
                    showSnackbar("Failed to load document count", "error");
                }
            }
        }

        if (loginUser) {
            const userName = `${loginUser.firstName} ${loginUser.lastName}`;
            initialFieldData.authorized_signatory_name = userName;

            const userDesignation = loginUser.designation;
            initialFieldData.authorized_signatory_designation = userDesignation;
        }
        setFieldData(initialFieldData);
    };

    const resetForm = () => {
        setEmployeeId("");
        setDocumentType("");
        setDocumentId(null);
        setFieldData({});
        setDocuments([]);
        setIsGenerating(false);
    };

    const handlePreview = () => {
        navigate("/home/documents/preview", {
            state: {
                id: documentId,
                employeeId,
                documentType,
                fieldData
            }
        });
    };

    const handleGenerateDocument = async () => {
        setIsGenerating(true);
        try {
            const response = await documentServices.generateDocument({
                id: documentId,
                userId: employeeId,
                documentType,
                fieldData
            });

            showSnackbar("Document Generated Successfully!", "success");
            resetForm();
            setTimeout(() => {
                navigate('/home/documents');
            }, 1500);

        } catch (error) {
            if (error.response) {
                const responseData = error.response.data;

                if (responseData instanceof Blob) {
                    try {
                        const text = await responseData.text();
                        const errorJson = JSON.parse(text);
                        showSnackbar(errorJson.message || "Failed to generate document", "error");
                    } catch (parseError) {
                        showSnackbar("Failed to generate document", "error");
                    }
                }
                else if (responseData && responseData.message) {
                    showSnackbar(responseData.message, "error");
                }
                else {
                    showSnackbar("Failed to generate document", "error");
                }
            } else {
                showSnackbar("Failed to generate document", "error");
            }
            setIsGenerating(false);
        }
    };

    const handleDocumentDownload = async (doc) => {
        setIsDownloading(true);
        setDownloadMessage(`Downloading ${DOCUMENT_TYPE[doc.documentType] ?? doc.documentType}...`);
        try {
            await documentServices.downloadDocument({
                id: doc.id,
                userId: doc.userId,
                documentType: doc.documentType,
                fieldData: doc.fieldData
            });

        } catch {
            showSnackbar("Failed to download document", "error");
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDeleteClick = (id) => {
        setDocumentToDelete(id);
        setOpenDialog(true);
    };

    const confirmDelete = async () => {
        if (!documentToDelete) return;

        try {
            await documentServices.deleteDocument(documentToDelete);
            setDocuments((prevDocs) => prevDocs.filter((doc) => doc.id !== documentToDelete));
            setOpenDialog(false);
            setDocumentToDelete(null);
            showSnackbar("Document deleted successfully", "success");
        } catch (error) {
            showSnackbar("Failed to delete document", "error");
            setOpenDialog(false);
        }
    };

    const SelectedForm = DocumentFormMap[documentType];

    const isFormValid = () => {
        const requiredFields = DocumentField(documentType);
        return requiredFields.every(field => fieldData[field] && fieldData[field] !== "");
    };

    const handleOpenDialog = () => {
        setOpenDocumentDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDocumentDialog(false);
    };

    return (
        <Box className="document-page-container">
            <PageHeader title={t('Documents')} />

            <Box className="document-layout">
                {/* Left Panel: Selection */}
                <Box className="document-selection-card">
                    <Typography className="document-section-title" variant="h6">
                        <FolderIcon /> {t('Document Selection')}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel>Employee</InputLabel>
                            <Select
                                value={employeeId}
                                label="Employee"
                                sx={{ textAlign: 'left' }}
                                onChange={(e) => {
                                    setEmployeeId(e.target.value);
                                    setDocumentId(null);
                                    setDocumentType("");
                                    setFieldData({});
                                }}
                            >
                                {employees.map(emp => (
                                    <MenuItem key={emp.id} value={emp.id}>
                                        {emp.firstName} {emp.lastName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth disabled={!employeeId}>
                            <InputLabel>Document Type</InputLabel>
                            <Select
                                value={documentType}
                                label="Document Type"
                                sx={{ textAlign: 'left' }}
                                onChange={(e) => handleDocumentTypeChange(e.target.value)}
                            >
                                {Object.entries(DOCUMENT_TYPE).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {employeeId && documents.length > 0 && (
                            <Button
                                variant="contained"
                                onClick={handleOpenDialog}
                                startIcon={<FolderIcon />}
                                endIcon={<OpenInNewIcon />}
                                fullWidth
                                sx={{
                                    borderRadius: 999,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    py: 1.5
                                }}
                            >
                                Past Documents
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Right Panel: Form Area */}
                <Box className="document-form-card">
                    {!documentType ? (
                        <Box className="document-empty-state">
                            <DescriptionRoundedIcon sx={{ fontSize: 64, mb: 2, opacity: 0.2 }} />
                            <Typography variant="h6">
                                {t('Please select an employee and document type to begin.')}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
                                {t('You can then fill in the required details and generate the document.')}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography className="document-section-title" variant="h6">
                                <DescriptionRoundedIcon /> {DOCUMENT_TYPE[documentType]} Details
                            </Typography>

                            {SelectedForm && (
                                <SelectedForm data={fieldData} setData={setFieldData} />
                            )}

                            <Box className="document-action-bar">
                                <Button
                                    variant="outlined"
                                    disabled={!isFormValid() || isGenerating}
                                    onClick={handlePreview}
                                    startIcon={<PreviewIcon />}
                                    sx={{ borderRadius: 999, px: 4 }}
                                >
                                    Preview
                                </Button>

                                {/* <Button
                                    variant="contained"
                                    disabled={!isFormValid() || isGenerating}
                                    onClick={handleGenerateDocument}
                                    startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                                    sx={{ borderRadius: 999, px: 4 }}
                                >
                                    {isGenerating ? "Generating..." : (documentId != null ? "Regenerate" : "Generate")}
                                </Button> */}
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            {/* Dialog for Employee Documents */}
            <Dialog
                open={openDocumentDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                slotProps={{ paper: { className: 'modern-dialog' } }}
            >
                <DialogTitle className="modern-dialog-title">
                    <span className="modern-dialog-title-text">Employee Documents</span>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDialog}
                        className="modern-dialog-close-btn"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className="modern-dialog-content">
                    {documents.length > 0 ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                            {documents.map((doc, index) => (
                                <Paper
                                    key={index}
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        justifyContent: "space-between",
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 2, sm: 0 },
                                        borderRadius: 3,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            boxShadow: "0 2px 0.5rem rgba(0,0,0,0.05)",
                                            borderColor: "primary.light"
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <DescriptionRoundedIcon sx={{ color: 'primary.main', opacity: 0.7 }} />
                                        <Typography fontWeight={600} color="text.primary">
                                            {DOCUMENT_TYPE[doc.documentType] ?? doc.documentType}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}>
                                        <Button
                                            size="small"
                                            startIcon={<PreviewIcon />}
                                            variant="outlined"
                                            onClick={() =>
                                                navigate("/home/documents/preview", {
                                                    state: {
                                                        id: doc.id,
                                                        employeeId: doc.userId,
                                                        documentType: doc.documentType,
                                                        fieldData: doc.fieldData
                                                    }
                                                })
                                            }
                                            sx={{ borderRadius: 2, flex: { xs: 1, sm: "none" }, minWidth: { xs: 0, sm: 'auto' } }}
                                        >
                                            View
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            variant="outlined"
                                            onClick={() => handleDocumentDownload(doc)}
                                            sx={{ borderRadius: 2, flex: { xs: 1, sm: "none" }, minWidth: { xs: 0, sm: 'auto' } }}
                                        >
                                            Download
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<DeleteIcon />}
                                            variant="outlined"
                                            color="error"
                                            onClick={() => handleDeleteClick(doc.id)}
                                            sx={{ borderRadius: 2, flex: { xs: 1, sm: "none" }, minWidth: { xs: 0, sm: 'auto' } }}
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ py: 4, textAlign: 'center', opacity: 0.6 }}>
                            <Typography>No documents found.</Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onConfirm={confirmDelete}
                title="Delete Confirmation"
                message="Are you sure you want to delete this document?"
                messageSecond="*This action cannot be undone."
                cancelLabel="Cancel"
                confirmLabel="Delete"
                isDelete={true}
            />
            {SnackbarComponent}
            <ActionProgressDialog open={isDownloading || isGenerating} message={isGenerating ? "Generating Document..." : downloadMessage} />
        </Box>

    );
};

export default DocumentForm;